import {createContext, ReactNode, useEffect, useReducer, useState} from 'react';
// utils
import {axiosInstance as axios} from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {

  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if(isValidating) {
      // enqueueSnackbar("Validating your credentials"); // Auth is called before Snackbar Provider is init., so this won't work
    }
  },[isValidating]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setIsValidating(true);

          setSession(accessToken);

          const response = await axios.post('/auth/me');
          const user = response.data;

          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user: user,
            },
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
        setIsValidating(false);
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize().then(x=>{});
  }, []);

  const login = async (email: string, password: string, csrfToken:string) => {
    const response = await axios.post('/auth', {
      u: email,
      p: password,
      from: "web-admin",
      _csrf: csrfToken,
    }, {
        headers: {
            'X-CSRF-Token': csrfToken,
        }
    });
    const { api_token } = response.data;
    const user = response.data;

    setSession(api_token);
    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, username: string) => {
    const response = await axios.post('/auth/register', {
      email: email,
      p: password,
      fname: firstName,
      lname: lastName,
      u: username,
      type: 'web',
    }); // edit auth.ts for type, registerform.tsx for fields
    /*
    const { accessToken, user } = response.data;
    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
    */
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: Types.Logout });
  };

  return (
    <>
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
    { isValidating && <div style={{position: "absolute", zIndex: 1000, top:0, right:0, padding:'5px 25px', background:"black", color:"white", borderBottomLeftRadius: '4px'}}>Re-validating your stored credentials...</div> }
    </>
  );

}

export { AuthContext, AuthProvider };
